import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { runningBundle, servedBundle } from '../utils/appVersion';

const CHECK_INTERVAL_MS = 60_000;
const RELOADED_FOR_KEY = 'flowy_reloadedForBundle';

// Kept outside React: MainLayout is mounted anew with every route.
let newBundle: string | null = null;
let lastCheck = Date.now();
let lastPathname: string | null = null;

const findNewBundle = async (running: string): Promise<string | null> => {
    if (newBundle || Date.now() - lastCheck < CHECK_INTERVAL_MS) return newBundle;
    lastCheck = Date.now();
    try {
        const served = await servedBundle();
        if (served && served !== running) newBundle = served;
    } catch {
        // offline: a later page change asks again
    }
    return newBundle;
};

const reloadFor = (bundle: string) => {
    try {
        // Once per bundle: a server that keeps naming a bundle this page does not end up with must not loop.
        if (sessionStorage.getItem(RELOADED_FOR_KEY) === bundle) return;
        sessionStorage.setItem(RELOADED_FOR_KEY, bundle);
    } catch {
        // storage blocked: reload all the same
    }
    window.location.reload();
};

/**
 * An open tab keeps running the bundle it was loaded with: clicking inside the app only fetches data,
 * so a fix deployed meanwhile never reaches it. The server's index.html is checked on a page change and
 * on coming back to the tab (at most once a minute); once it names another bundle, the page change is
 * done as a full page load. Never in place — a half-filled form is not lost, and a change of query
 * parameters only (search, filters, paging) is not a page change.
 */
export const useNewVersionReload = () => {
    const {pathname} = useLocation();

    useEffect(() => {
        const running = runningBundle();
        if (!running) return;

        const pageChanged = lastPathname !== null && lastPathname !== pathname;
        lastPathname = pathname;
        if (!pageChanged) return;

        findNewBundle(running).then(bundle => {
            if (bundle) reloadFor(bundle);
        });
    }, [pathname]);

    useEffect(() => {
        const running = runningBundle();
        if (!running) return;

        const onReturn = () => {
            if (document.visibilityState === 'visible') findNewBundle(running);
        };
        document.addEventListener('visibilitychange', onReturn);
        window.addEventListener('focus', onReturn);
        return () => {
            document.removeEventListener('visibilitychange', onReturn);
            window.removeEventListener('focus', onReturn);
        };
    }, []);
};
